import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect, useCallback } from 'react'
import type { ApplicationField, ApplicationFieldType } from '@/types'
import { adminSectionLibraryService } from '../services/admin.sectionLibrary.service'
import { adminFieldLibraryService } from '../services/admin.fieldLibrary.service'
import type { SectionLibraryFormValues } from '../types'

function generateId(): string {
  return crypto.randomUUID()
}

function defaultFieldForType(type: ApplicationFieldType): Omit<ApplicationField, 'id' | 'order'> {
  const base: Omit<ApplicationField, 'id' | 'order'> = {
    type,
    label: fieldTypeLabel(type),
    required: false,
  }
  switch (type) {
    case 'linear_scale':
      return { ...base, min: 1, max: 5, minLabel: '', maxLabel: '' }
    case 'dropdown':
    case 'multiple_choice':
    case 'checkbox_group':
      return { ...base, options: ['Option 1', 'Option 2'] }
    case 'file_upload':
      return { ...base, maxFileSizeMB: 10, acceptedFileTypes: [] }
    case 'text_list':
      return base
    default:
      return base
  }
}

function fieldTypeLabel(type: ApplicationFieldType): string {
  const labels: Record<ApplicationFieldType, string> = {
    short_text: 'Short Text',
    long_text: 'Long Text',
    email: 'Email',
    phone: 'Phone',
    url: 'URL',
    number: 'Number',
    date: 'Date',
    dropdown: 'Dropdown',
    multiple_choice: 'Multiple Choice',
    checkbox_group: 'Checkbox Group',
    linear_scale: 'Linear Scale',
    file_upload: 'File Upload',
    mailing_list: 'Mailing List Opt-In',
    text_list: 'Text List',
    payment: 'Payment',
    notice: 'Notice',
  }
  return labels[type]
}

interface UseAdminSectionTemplateResult {
  loading: boolean
  saving: boolean
  error: string | null
  isDirty: boolean
  metadata: SectionLibraryFormValues
  fields: ApplicationField[]
  setMetadata: (patch: Partial<SectionLibraryFormValues>) => void
  addField: (type: ApplicationFieldType) => void
  addFieldFromLibrary: (libraryId: string) => Promise<void>
  updateField: (fieldId: string, patch: Partial<ApplicationField>) => void
  deleteField: (fieldId: string) => void
  reorderFields: (orderedIds: string[]) => void
  save: () => Promise<void>
}

export function useAdminSectionTemplate(templateId: string): UseAdminSectionTemplateResult {
  const [metadata, setMetadataState] = useState<SectionLibraryFormValues>({
    sectionId: '',
    title: '',
    description: '',
    category: 'General',
  })
  const [fields, setFields] = useState<ApplicationField[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    if (!templateId) return
    setLoading(true)
    adminSectionLibraryService
      .getById(templateId)
      .then((tpl) => {
        if (!tpl) {
          setError(withErrorCode('Template not found', 'app/validation'))
          return
        }
        setMetadataState({
          sectionId: tpl.sectionId,
          title: tpl.title,
          description: tpl.description ?? '',
          category: tpl.category ?? 'General',
        })
        setFields(
          (tpl.fields ?? []).map((f, i) => ({
            ...f,
            id: f.id && f.id.length > 0 ? f.id : generateId(),
            order: i,
          }))
        )
      })
      .catch((err) => setError(formatUserFacingError(err, 'Failed to load template')))
      .finally(() => setLoading(false))
  }, [templateId])

  const markDirty = useCallback(() => setIsDirty(true), [])

  const setMetadata = useCallback(
    (patch: Partial<SectionLibraryFormValues>) => {
      setMetadataState((prev) => ({ ...prev, ...patch }))
      markDirty()
    },
    [markDirty]
  )

  const addField = useCallback(
    (type: ApplicationFieldType) => {
      setFields((prev) => [
        ...prev,
        { id: generateId(), order: prev.length, ...defaultFieldForType(type) },
      ])
      markDirty()
    },
    [markDirty]
  )

  const addFieldFromLibrary = useCallback(
    async (libraryId: string) => {
      const libraryField = await adminFieldLibraryService.getById(libraryId)
      if (!libraryField) return
      setFields((prev) => {
        const newField: ApplicationField = {
          id: generateId(),
          fieldLibraryId: libraryId,
          type: libraryField.type,
          label: libraryField.label,
          description: libraryField.description,
          placeholder: libraryField.placeholder,
          required: false,
          order: prev.length,
          options: libraryField.options,
          min: libraryField.min,
          max: libraryField.max,
          minLabel: libraryField.minLabel,
          maxLabel: libraryField.maxLabel,
          maxFileSizeMB: libraryField.maxFileSizeMB,
          acceptedFileTypes: libraryField.acceptedFileTypes,
        }
        return [...prev, newField]
      })
      markDirty()
    },
    [markDirty]
  )

  const updateField = useCallback(
    (fieldId: string, patch: Partial<ApplicationField>) => {
      setFields((prev) => prev.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)))
      markDirty()
    },
    [markDirty]
  )

  const deleteField = useCallback(
    (fieldId: string) => {
      setFields((prev) => prev.filter((f) => f.id !== fieldId))
      markDirty()
    },
    [markDirty]
  )

  const reorderFields = useCallback(
    (orderedIds: string[]) => {
      setFields((prev) => {
        const map = Object.fromEntries(prev.map((f) => [f.id, f]))
        return orderedIds.map((id, index) => ({ ...map[id]!, order: index }))
      })
      markDirty()
    },
    [markDirty]
  )

  const save = useCallback(async () => {
    if (!templateId) return
    setSaving(true)
    setError(null)
    try {
      await adminSectionLibraryService.updateAll(templateId, { ...metadata, fields })
      setIsDirty(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save template'))
    } finally {
      setSaving(false)
    }
  }, [templateId, metadata, fields])

  return {
    loading,
    saving,
    error,
    isDirty,
    metadata,
    fields,
    setMetadata,
    addField,
    addFieldFromLibrary,
    updateField,
    deleteField,
    reorderFields,
    save,
  }
}
